/**
 * Fictional example content for product visuals. Every visual that renders this
 * data must carry a visible "Example review" / "Illustrative example" label.
 */

export type EvidenceStatus = 'verified' | 'partial' | 'missing' | 'requested'

/** Homepage hero: one requirement traced to its evidence, gap and follow-up. */
export const HERO_REVIEW = {
  company: 'Lumen Health',
  context: 'Series A · Healthtech · 6 files received',
  requirement: { label: 'Requirement', text: 'Recurring revenue of at least $1M ARR' },
  document: { label: 'Supporting document', text: 'Q2 board deck, p.7 — ARR stated as $1.4M', source: 'Board_Deck_Q2.pdf' },
  question: { label: 'Unanswered question', text: 'How much of that ARR comes from the two largest customers?' },
  followUp: { label: 'Follow-up needed', text: 'Request a customer revenue breakdown from the founder', status: 'Request sent · awaiting reply' },
  decision: 'Reviewer decision on hold until the follow-up is answered',
} as const

export const MEMO_EXAMPLE = {
  company: 'Northwind Grid',
  context: 'Seed · Climate infrastructure',
  fit: [
    { criterion: 'Stage: Seed to Series A', status: 'verified' as EvidenceStatus, evidence: 'Raising $4M seed · Deck p.2' },
    { criterion: 'Sector: Climate infrastructure', status: 'verified' as EvidenceStatus, evidence: 'Grid storage software · Deck p.4' },
    { criterion: 'Revenue: at least $500K ARR', status: 'partial' as EvidenceStatus, evidence: '$620K ARR stated · not yet reconciled to the model' },
  ],
  openQuestion: 'What share of ARR comes from the two pilot utilities?',
}

export const VENDOR_EXAMPLE = {
  vendors: ['Fieldline', 'Orbit Systems'],
  requirements: [
    { requirement: 'Single sign-on (SAML)', statuses: ['verified', 'verified'] as EvidenceStatus[] },
    { requirement: 'Current SOC 2 Type II report', statuses: ['verified', 'requested'] as EvidenceStatus[] },
    { requirement: 'EU data residency', statuses: ['verified', 'partial'] as EvidenceStatus[] },
    { requirement: '99.9% uptime SLA', statuses: ['partial', 'verified'] as EvidenceStatus[] },
  ],
  note: { author: 'Reviewer note', text: 'Orbit Systems: SOC 2 report requested from vendor. Hold approval until received.' },
}

export const WORKSTREAM_EXAMPLE = {
  target: 'Project Kestrel',
  workstreams: [
    { name: 'Commercial', owner: 'J. Park', status: 'complete', finding: 'Top 3 customers are 48% of revenue' },
    { name: 'Financial', owner: 'M. Chen', status: 'complete', finding: 'FY23 deferred revenue restated' },
    { name: 'Technical', owner: 'R. Osei', status: 'review', finding: 'Core service on an unsupported framework' },
    { name: 'Organizational', owner: 'A. Diaz', status: 'complete', finding: 'Two key engineers without retention' },
    { name: 'Strategic', owner: 'L. Novak', status: 'complete', finding: 'Overlaps an existing roadmap item' },
  ],
  unresolved: 'Does the FY23 restatement change the purchase price adjustment?',
}

export const SCORECARD_EXAMPLE = {
  applicant: 'Tidewater Labs',
  rubric: [
    { criterion: 'Team', score: 4 },
    { criterion: 'Impact', score: 5 },
    { criterion: 'Feasibility', score: 3 },
    { criterion: 'Budget fit', score: 4 },
  ],
  max: 5,
  shortlist: [
    { name: 'Tidewater Labs', total: 16, reason: 'Strong impact case; county pilot letter attached' },
    { name: 'Brightfold', total: 15, reason: 'Experienced team; budget needs clarification' },
    { name: 'Quarry AI', total: 14, reason: 'Clear feasibility plan; narrower impact' },
  ],
}

export const RISK_EXAMPLE = {
  counterparty: 'Meridian Logistics',
  rows: [
    { area: 'Governance', finding: 'Board minutes for last 4 quarters', status: 'verified' as EvidenceStatus, owner: 'K. Shah', next: 'None' },
    { area: 'Financial health', finding: 'Audited FY25 statements', status: 'verified' as EvidenceStatus, owner: 'K. Shah', next: 'None' },
    { area: 'Cybersecurity', finding: 'Pen test older than 12 months', status: 'requested' as EvidenceStatus, owner: 'D. Ruiz', next: 'Request 2026 summary' },
    { area: 'Compliance', finding: 'No ISO 27001 evidence provided', status: 'missing' as EvidenceStatus, owner: 'D. Ruiz', next: 'Ask for certificate or equivalent' },
  ],
}

export const TIMELINE_EXAMPLE = {
  recipient: 'Harbor Water Co-op',
  events: [
    { label: 'Original commitment', date: 'Jan 2026', text: 'Pilot live at 3 sites by 30 Sep', tone: 'neutral' },
    { label: 'Latest update', date: 'Oct 2026', text: '2 of 3 sites live. Third site delayed to December.', tone: 'flag' },
    { label: 'Next review', date: '15 Dec 2026', text: 'Follow-up assigned to the grant officer', tone: 'next' },
  ] as const,
}
